import type { GapDraftView, GapReviewExecutionSummary } from '@momentum/types';

import { KEEP_STATUS } from './queue';

/**
 * The final-review pass is the one part of this surface that is not about a run.
 *
 * A gap is kept because a reviewer decided it was worth a second look, and the
 * execution it happened to be found in says nothing about that. So the kept list
 * is drawn across every run — and the badge over it has to count the same way,
 * or the tab would promise four gaps and the page would show eleven.
 */

interface KeptAcrossRunsInput {
  /** Every run of the company, each carrying its own database-counted tally. */
  executions: GapReviewExecutionSummary[];
  /** The run whose drafts are loaded, if any. */
  selectedExecutionId: string | undefined;
  /** That run's drafts. Undefined before they arrive. */
  selectedRunDrafts: GapDraftView[] | undefined;
}

/**
 * How many drafts are waiting in final review, over every run.
 *
 * The runs are counted in the database — `keptCount` on each summary — because
 * the surface never loads more than one run's drafts, and a badge that had to
 * fetch the corpus to draw itself would undo the whole reason the queue is
 * scoped.
 *
 * The selected run is the exception, and is counted from its drafts instead:
 * those are the ones being decided right now, and a verdict is applied
 * optimistically, so taking that run's number from the summary would leave the
 * badge a round trip behind the keypress that changed it.
 */
export function countKeptAcrossRuns({
  executions,
  selectedExecutionId,
  selectedRunDrafts,
}: KeptAcrossRunsInput): number {
  const liveKeptCount = selectedRunDrafts?.filter(
    (draft) => draft.review.status === KEEP_STATUS
  ).length;

  return executions.reduce((total, execution) => {
    const isLoadedRun =
      execution.executionId === selectedExecutionId &&
      liveKeptCount !== undefined;
    return total + (isLoadedRun ? liveKeptCount : execution.keptCount);
  }, 0);
}
